/**
 * nav.ts (CRUMB-NAV-01, 2026-09-02) — ONE map of where things live, shared by
 * the rail (Sidebar) and the top-bar breadcrumb (Header). Before this the two
 * kept separate copies and drifted: the breadcrumb said "The Day" for the top
 * group while the rail never showed that label at all (Seth: "Unsure why the
 * first thing says The Day"). Now a group shows in the breadcrumb ONLY when the
 * rail shows it too, and both read from here.
 *
 * NAV-AUDIT-01 (Seth 2026-08-26 "make sure it's optimized"): three groups that
 * mirror how the company actually works, ordered by how often each is touched.
 */
import {
  LayoutDashboard,
  Calendar,
  Users,
  Inbox,
  Megaphone,
  BarChart3,
  Settings,
  ShieldCheck,
  Package,
  Hammer,
  FileText,
  Receipt,
  ShoppingCart,
  HardHat,
  Warehouse,
  Truck,
  Zap,
  Mail,
  PenTool,
} from "lucide-react";

export type NavIcon = React.ComponentType<{ className?: string; strokeWidth?: number }>;

export type NavItem = {
  to: string;
  label: string;
  icon: NavIcon;
};

export type NavGroup = {
  key: "day" | "sell" | "company";
  /** null = the top of the rail, shown without a heading (and so without a breadcrumb segment). */
  label: string | null;
  items: NavItem[];
};

// THE DAY — dispatch + office live here, morning to close. Top of the rail,
// no heading: it's simply where the app opens.
const dayNav: NavItem[] = [
  { to: "/today", label: "Today", icon: LayoutDashboard },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/board", label: "Board", icon: Calendar },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/agreements", label: "Memberships", icon: ShieldCheck },
];

// SELL & BUILD — the money pipeline in its real order: proposal → job →
// warehouse pull → invoice → the parts that fed it.
const sellNav: NavItem[] = [
  { to: "/quotes", label: "Proposals", icon: FileText },
  { to: "/jobs", label: "Jobs", icon: Hammer },
  { to: "/warehouse", label: "Warehouse", icon: Warehouse },
  { to: "/invoices", label: "Invoices", icon: Receipt },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
];

// COMPANY — managing the business, not running the day. Automations moved off
// the sidebar (it's comms CONFIG — /automations still works, linked from
// Settings) to keep this rail an honest map of daily work.
const companyNav: NavItem[] = [
  { to: "/team", label: "Team", icon: HardHat },
  { to: "/items", label: "Pricebook", icon: Package },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
];

export const NAV_GROUPS: NavGroup[] = [
  { key: "day", label: null, items: dayNav },
  { key: "sell", label: "Sell & Build", items: sellNav },
  { key: "company", label: "Company", items: companyNav },
];

/**
 * Pages that belong to a group but aren't on the rail — record pages and
 * config screens. `parent` is the list page the crumb links back to (a
 * dispatch lives on the Board; an invoice under Invoices).
 */
export const OFF_RAIL: Record<string, { group: NavGroup["key"]; label: string; icon: NavIcon; parent?: string }> = {
  dispatch: { group: "day", label: "Dispatch", icon: Calendar, parent: "/board" },
  invoice: { group: "sell", label: "Invoice", icon: Receipt, parent: "/invoices" },
  vendors: { group: "sell", label: "Vendors", icon: Truck },
  automations: { group: "company", label: "Comms Automations", icon: Zap, parent: "/settings" },
  "email-builder": { group: "company", label: "Email Builder", icon: Mail, parent: "/marketing" },
  "editor-spike": { group: "company", label: "Editor Spike", icon: PenTool },
};

export function groupOf(key: NavGroup["key"]): NavGroup {
  return NAV_GROUPS.find((g) => g.key === key) ?? NAV_GROUPS[0];
}

/** The rail entry for a top-level route segment ("jobs" → Jobs), if it has one. */
export function railItemFor(root: string): { group: NavGroup; item: NavItem } | null {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((i) => i.to === `/${root}`);
    if (item) return { group, item };
  }
  return null;
}

/** Browser-tab / breadcrumb name for a top-level route segment. */
export function pageLabelFor(root: string): string {
  return railItemFor(root)?.item.label ?? OFF_RAIL[root]?.label ?? "Pegasus";
}
